import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { serviceDetails } from '../data/services';
import { BUSINESS } from '../data/business';
import PageMeta from '../components/PageMeta';

interface Props {
  onContactClick: () => void;
}

const ServicesPage = ({ onContactClick }: Props) => {
  // Erstes Item offen: zeigt sofort Preis + Beschreibung und erklärt das Interaktionsmuster
  const [open, setOpen] = useState<number | null>(0);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'IT-Beratungsleistungen AK TechSolutions',
    itemListElement: serviceDetails.map((s, i) => {
      // schema.org verlangt numerische Preise — "nach Aufwand" bekommt kein Offer
      const minPrice = s.price.match(/\d+/);
      return {
        '@type': 'ListItem',
        position: i + 1,
        item: {
          '@type': 'Service',
          name: s.name,
          description: s.description,
          provider: {
            '@type': 'LocalBusiness',
            name: BUSINESS.name,
            url: BUSINESS.url,
          },
          ...(minPrice && {
            offers: {
              '@type': 'Offer',
              priceSpecification: {
                '@type': 'PriceSpecification',
                minPrice: Number(minPrice[0]),
                priceCurrency: 'EUR',
              },
            },
          }),
        },
      };
    }),
  };

  return (
    <>
    <PageMeta
      title="Leistungen & Preise"
      description="IT-Beratung, SmartHome, Netzwerk & WLAN, Hardware-Kaufberatung und 3D-Druck – faire Preise, keine versteckten Kosten."
      path="/leistungen"
    />
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
    </Helmet>
    <section className="min-h-[80vh] px-8 py-[clamp(40px,7vw,72px)]">
      <div className="mx-auto max-w-[800px]">
        <div className="mb-10">
          <h1 className="mb-3 text-[clamp(1.5rem,3vw,2.25rem)] font-bold text-white">
            Leistungen & Preise
          </h1>
          <p className="m-0 text-[15px] text-ak-muted">
            Klicken Sie auf eine Leistung, um mehr zu erfahren.
          </p>
        </div>

        <div className="flex flex-col gap-3">
          {serviceDetails.map((s, i) => (
            <div
              key={i}
              className={`overflow-hidden rounded-[18px] border transition-[background-color,border-color,box-shadow] duration-[250ms] ${
                open === i
                  ? 'border-ak-sky/25 bg-ak-dark shadow-[0_8px_32px_rgba(0,0,0,0.25)]'
                  : 'border-white/5 bg-ak-dark/60'
              }`}
            >
              <button
                onClick={() => setOpen(open === i ? null : i)}
                aria-expanded={open === i}
                aria-controls={`service-panel-${i}`}
                className="flex w-full cursor-pointer items-center justify-between gap-3 border-0 bg-transparent px-[22px] py-[18px] text-left font-sans"
              >
                <div className="flex items-center gap-3.5">
                  <div
                    className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl transition-colors duration-[250ms] ${
                      open === i ? 'bg-ak-sky/[0.13]' : 'bg-ak-yellow/[0.08]'
                    }`}
                  >
                    <i className={`pi ${s.icon} text-[1.4rem] ${open === i ? 'text-ak-sky' : 'text-ak-yellow'}`}></i>
                  </div>
                  <span className="text-base font-bold text-white">{s.name}</span>
                </div>
                <div className="flex shrink-0 items-center gap-3">
                  {open !== i && (
                    <span className="text-sm font-medium text-ak-sky">{s.price}</span>
                  )}
                  <span
                    className={`flex h-7 w-7 items-center justify-center rounded-full transition-colors duration-300 ${
                      open === i ? 'bg-ak-sky/[0.13]' : 'bg-white/[0.06]'
                    }`}
                  >
                    <i className={`pi pi-${open === i ? 'minus' : 'plus'} text-[11px] ${open === i ? 'text-ak-sky' : 'text-ak-dim'}`}></i>
                  </span>
                </div>
              </button>

              {open === i && (
                <div id={`service-panel-${i}`} role="region" className="px-[22px] pb-[22px]">
                  <p className="mb-4 text-sm leading-[1.7] text-white/80">
                    {s.description}
                  </p>
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <span className="text-[15px] font-bold text-ak-sky">{s.price}</span>
                    <button onClick={onContactClick} className="btn-primary btn-sm">
                      <i className="pi pi-envelope"></i> Kontakt aufnehmen
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        <p className="mt-8 text-center text-xs leading-[1.7] text-ak-dim">
          Alle Preise sind Endpreise. Gemäß § 19 UStG wird keine Umsatzsteuer ausgewiesen.<br />
          Gerne erstelle ich Ihnen ein individuelles Angebot.
        </p>
        <div className="mt-5 text-center">
          <button onClick={onContactClick} className="btn-primary text-sm">
            <i className="pi pi-envelope"></i> Kontakt aufnehmen
          </button>
        </div>
      </div>
    </section>
    </>
  );
};

export default ServicesPage;
